import React, { useMemo } from 'react'

import { isTeamExternalIntegrationGcp } from 'components/common/mapped-entities/gcp/authentication/gcp-integration-utils'
import { GcpNewAuthenticationForm } from 'components/common/mapped-entities/gcp/authentication/GcpNewAuthenticationForm'
import PageSection from 'components/common/PageSection'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useGetGcpMetadata } from 'queries/gcp/use-get-gcp-metadata'
import { type TeamExternalIntegrationGcp } from 'types/api/status-checks/status-check'
import { messageFromResponseError } from 'utils/strings'
import { type OnSuccessExternalIntegration } from 'utils/team-external-integrations/team-external-integrations-util'

export function GcpNewAuthenticationContainer({
  isEnabled,
  onSuccess
}: {
  isEnabled: boolean
  onSuccess?: OnSuccessExternalIntegration
}) {
  const {
    externalIntegrationsError,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    teamExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const {
    data: gcpMetadataAuth,
    error: errorGcpMetadataAuth,
    isError: isErrorGcpMetadataAuth,
    isLoading: isLoadingGcpMetadataAuth
  } = useGetGcpMetadata({
    enabled: isEnabled
  })

  const templateUrl = gcpMetadataAuth?.deploymentTemplateUrl

  const gcpExternalIntegrations = useMemo(() => {
    if (!isEnabled || !teamExternalIntegrations) {
      return []
    }

    const gcpIntegrations = teamExternalIntegrations.filter(
      (integration) => isTeamExternalIntegrationGcp(integration)
      // TODO: when we upgrade TypeScript to 5.x, we can remove the typecast
    ) as TeamExternalIntegrationGcp[]

    return gcpIntegrations
  }, [isEnabled, teamExternalIntegrations])

  const isLoading = isFetchingExternalIntegrations || isLoadingGcpMetadataAuth
  const hasError = hasErrorExternalIntegrations || isErrorGcpMetadataAuth
  const errorMessage = hasErrorExternalIntegrations
    ? messageFromResponseError(
        externalIntegrationsError,
        'There was an issue while retrieving External Integrations'
      )
    : messageFromResponseError(
        errorGcpMetadataAuth,
        'Failed to fetch GCP authentication metadata'
      )

  return (
    <PageSection
      css={{ alignItems: 'start' }}
      errorMessage={errorMessage}
      hasError={hasError}
      isLoading={isLoading}
      isLoadingOverlayFullScreen={false}
    >
      {() => {
        if (!templateUrl) {
          throw new Error(
            'There was an error loading the page: unexpected response for GCP authentication metadata'
          )
        }

        return (
          <GcpNewAuthenticationForm
            gcpExternalIntegrations={gcpExternalIntegrations}
            templateUrl={templateUrl}
            onSuccess={onSuccess}
          />
        )
      }}
    </PageSection>
  )
}
